import { RefObject, useEffect, useState } from 'react';
import { Form } from '@bancobarigui/barihaus';

import { AddressData } from '@/shared/types/AddressData';
import { useErrorHandler } from '@/shared/hooks/useErrorHandler';
import { useAlertContext } from '@/contexts/alert.context';
import { inspectionService } from '@/services/inspection.service';
import { propertyTypeService } from '@/services/propertyType.service';
import { inspectionTypeService } from '@/services/inspectionType.service';
import { AddressFormRef } from '@/components/AddressForm';

export interface ContactFormData {
  contactName: string;
  email: string;
  contactType: number;
  mobilePhone?: string;
  residentialPhone?: string;
  commercialPhone?: string;
}

export type ItemFormData = AddressData & {
  inspectionReason: string;
  inspectionType: string;
  propertyType: string;
  observation?: string;
  landArea?: number;
  builtArea?: number;
  privateArea?: number;
  iptuNumber?: string;
  registrationNumber?: string;
  registryOfficeNumber?: string;
  addContactInfo?: boolean;
  contacts?: ContactFormData[];
  inspectionTypeLabel?: string;
  propertyTypeLabel?: string;
  isAutomaticReport?: boolean;
};

interface UseItemModalProps {
  onClose: () => void;
  onSubmit: (data: ItemFormData) => void;
  addressFormRef: RefObject<AddressFormRef>;
}

export function useItemModal({ onSubmit, onClose, addressFormRef }: UseItemModalProps) {
  const [form] = Form.useForm();
  const { handleError } = useErrorHandler();
  const { notify } = useAlertContext();
  const [inspectionTypes, setInspectionTypes] = useState<
    {
      value: string;
      label: string;
      disabled: boolean;
      requiresInspector: boolean;
    }[]
  >([]);
  const [propertyTypes, setPropertyTypes] = useState<
    {
      value: string;
      label: string;
      disabled: boolean;
    }[]
  >([]);
  const [reasons, setReasons] = useState<
    {
      value: string;
      label: string;
      disabled: boolean;
    }[]
  >([]);

  const handleFormSubmit = async (values: ItemFormData) => {
    try {
      if (!addressFormRef.current?.isCepValidated()) {
        notify({
          message: 'CEP não encontrado. Informe um CEP válido.',
          type: 'error',
        });
        return;
      }

      const selectedInspectionType = inspectionTypes.find((item) => item.value === values.inspectionType);
      const inspectionTypeLabel = selectedInspectionType?.label || '';
      const propertyTypeLabel =
        propertyTypes.find((item) => item.value === values.propertyType)?.label || '';
      const isAutomaticReport = selectedInspectionType ? !selectedInspectionType.requiresInspector : false;

      const dataWithLabels: ItemFormData = {
        ...values,
        inspectionTypeLabel,
        propertyTypeLabel,
        isAutomaticReport,
      };

      await onSubmit(dataWithLabels);
      form.resetFields();
      onClose();
    } catch (error: any) {
      if (error?.message === 'DUPLICATE_LOCATION') return;
      handleError(error, 'Erro ao adicionar item');
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await propertyTypeService.getAllPropertyTypes();

      setPropertyTypes(
        response.map((item) => ({
          value: item.id,
          label: item.name,
          disabled: !item.isActive,
        }))
      );
    } catch (error) {
      handleError(error, 'Erro ao buscar os dados dos Tipos de Imoveis.');
    }
  };

  const fetchInspections = async () => {
    try {
      const response = await inspectionTypeService.getAllInspectionTypes();

      setInspectionTypes(
        response.map((item) => ({
          value: item.id,
          disabled: false,
          label: item.name,
          requiresInspector: item.requiresInspector,
        }))
      );
    } catch (error) {
      handleError(error, 'Erro ao buscar os dados dos Tipos de Inspeção.');
    }
  };

  const fetchReasons = async () => {
    try {
      const response = await inspectionService.getAllInspectionReasons();

      setReasons(
        response.map((item) => ({
          value: item.id,
          label: item.name,
          disabled: !item.isActive,
        }))
      );
    } catch (error) {
      handleError(error, 'Erro ao buscar motivos de inspeção.');
    }
  };

  const fetchData = async () => {
    await fetchReasons();
    await fetchProperties();
    await fetchInspections();
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    form,
    reasons,
    propertyTypes,
    inspectionTypes,
    handleFormSubmit,
  };
}
